import React, { useRef, useEffect } from 'react';
import PropTypes from '../../../node_modules/prop-types/index.js';
import { noop } from '../../utils/functions.js';
import { isIE } from '../../utils/platforms.js';
import safeCustomStyle from '../../utils/safeCustomStyle.js';
import { OPTION_HEIGHT, LISTBOX_MAX_HEIGHT } from './constants.js';
import { DropdownMenuWrapper, DropdownItem } from './styledComponents.js';
import useTheme from '../../hooks/useTheme.js';

var LIST_ROLE = isIE() ? 'list' : 'listbox';
var LIST_ITEM_ROLE = isIE() ? 'listitem' : 'option';

var DropdownMenu = function DropdownMenu(_ref) {
  var id = _ref.id,
      ariaRequired = _ref.ariaRequired,
      handleListboxBlur = _ref.handleListboxBlur,
      handleKeyboardEvent = _ref.handleKeyboardEvent,
      handleItemClick = _ref.handleItemClick,
      dropdownItems = _ref.dropdownItems,
      placeholder = _ref.placeholder,
      selected = _ref.selected,
      customStyle = _ref.customStyle;
  var theme = useTheme();

  var _safeCustomStyle = safeCustomStyle(customStyle),
      dropdownMenuStyle = _safeCustomStyle.dropdownMenuStyle; // focus is required on `ul` so that hover effects work and arrow keys work


  var listboxRef = useRef(null);
  useEffect(function () {
    listboxRef.current.focus();
  }, []); // effect responsible for scrolling listbox so that selected option is always in view

  useEffect(function () {
    var _listboxRef$current = listboxRef.current,
        scrollHeight = _listboxRef$current.scrollHeight,
        clientHeight = _listboxRef$current.clientHeight; // if scrollheight of listbox is greater than max height, apply logic

    if (scrollHeight > clientHeight) {
      var elementBottom = (selected + 1) * OPTION_HEIGHT;

      if (elementBottom + OPTION_HEIGHT > LISTBOX_MAX_HEIGHT) {
        listboxRef.current.scrollTop = elementBottom + OPTION_HEIGHT - LISTBOX_MAX_HEIGHT;
      }
    }
  }, [selected]);

  var _ref2 = dropdownItems[selected] ? dropdownItems[selected] : {
    value: ''
  },
      value = _ref2.value;

  return /*#__PURE__*/React.createElement(DropdownMenuWrapper, {
    id: "".concat(id, "-listbox"),
    dropdownMenuStyle: dropdownMenuStyle,
    tabIndex: "-1",
    role: LIST_ROLE,
    "aria-required": ariaRequired,
    "aria-labelledby": id,
    "aria-activedescendant": "".concat(id, "-item-").concat(value),
    ref: listboxRef,
    onBlur: handleListboxBlur,
    onKeyDown: handleKeyboardEvent,
    theme: theme,
    "data-testid": "dropdown-list"
  }, dropdownItems.map(function (elem, i) {
    // Remove placeholder item from list if exist
    if (placeholder && i === 0) {
      return null;
    }

    return /*#__PURE__*/React.createElement(DropdownItem, {
      id: "".concat(id, "-item-").concat(elem.value),
      key: "item-".concat(elem.label),
      role: !elem.disabled ? LIST_ITEM_ROLE : 'none'
      /* identifies each selectable element */
      ,
      "aria-selected": selected === i
      /* indicates option that is selected and visually styled as so */
      ,
      onClick: function onClick(e) {
        handleItemClick(e, i);
      },
      isSelected: selected === i,
      className: elem.disabled ? 'disabled' : '',
      theme: theme
    }, elem.label);
  }));
};

process.env.NODE_ENV !== "production" ? DropdownMenu.propTypes = {
  /** ID of DOM element for dropdown */
  id: PropTypes.string.isRequired,

  /** global attribute aria-required */
  ariaRequired: PropTypes.bool,

  /** Dropdown menu items */
  dropdownItems: PropTypes.arrayOf(PropTypes.shape({
    /** value for the item, equivalent to option value in select element */
    value: PropTypes.string,

    /** label for the item, equivalent to option label in select element */
    label: PropTypes.string.isRequired,

    /** Item is not selectable if it is disabled */
    disabled: PropTypes.bool
  })),

  /** default placeholder text */
  placeholder: PropTypes.string,

  /** function to call onBlur of listbox */
  handleListboxBlur: PropTypes.func,

  /** function to call onKeyDown of listbox */
  handleKeyboardEvent: PropTypes.func,

  /** function to call onClick of a dropdown item */
  handleItemClick: PropTypes.func,

  /** index of selected item, default is 0 */
  selected: PropTypes.number,

  /** custom styles */
  customStyle: PropTypes.shape({
    dropdownMenuStyle: PropTypes.object // styling for Dropdown Input items

  })
} : void 0;
DropdownMenu.defaultProps = {
  dropdownItems: [],
  ariaRequired: false,
  placeholder: '',
  handleListboxBlur: noop,
  handleKeyboardEvent: noop,
  handleItemClick: noop,
  selected: 0,
  customStyle: {
    dropdownMenuStyle: {}
  }
};
process.env.NODE_ENV !== "production" ? DropdownMenu.displayName = 'DropdownMenu' : void 0;
var DropdownMenu$1 = DropdownMenu;

export { DropdownMenu$1 as default };
